import React, { useState } from "react";
import { callAdmin, fmtUntil, RestrictForm } from "./shared";

const Badge = ({ color, children, testId }) => (
  <span data-testid={testId} className="px-2 py-0.5 rounded-md text-[10px] font-heading uppercase tracking-widest border" style={{ color, borderColor: color + "88" }}>{children}</span>
);

// One search result with the actions the current staff member is allowed to use.
export function PlayerCard({ p, perms, onChanged }) {
  const [confirmDelete, setConfirmDelete] = useState("");
  const can = (x) => perms.includes(x);
  const id = p.user_id;
  const protectedTarget = p.role === "admin" || (p.role === "moderator" && !perms.includes("roles.manage"));

  return (
    <div data-testid={`admin-player-${id}`} className="rounded-xl border border-[#39194D] bg-[#12082B] p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-heading text-lg neon-cyan">{p.account_name || "(no name)"}</span>
        {p.role && <Badge color="#FFE600" testId={`admin-player-role-${id}`}>{p.role}</Badge>}
        {p.lock && <Badge color="#FF007F" testId={`admin-player-locked-${id}`}>locked {fmtUntil(p.lock)}</Badge>}
        {p.chat_ban && <Badge color="#FF8A00" testId={`admin-player-chatban-${id}`}>chat ban {fmtUntil(p.chat_ban)}</Badge>}
        <span className="ml-auto font-mono text-[10px] text-[#A89BCC]">
          {p.email && <>{p.email} · </>}{p.wrestlers} wrestlers · {Number(p.handshakes || 0).toLocaleString()} HS · last seen {p.last_seen ? new Date(p.last_seen).toLocaleString() : "never"}
        </span>
      </div>
      {protectedTarget ? (
        <div className="font-mono text-xs text-[#A89BCC]">This account is protected.</div>
      ) : (
        <>
          {can("chat.ban") && (
            <div className="space-y-1">
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#A89BCC]">Chat ban</div>
              <RestrictForm testId={`chat-ban-${id}`} label="Ban from chat"
                onApply={async (b) => (await callAdmin("post", `/players/${id}/chat-ban`, b, "Chat ban issued")) && onChanged()} />
              {p.chat_ban && (
                <button data-testid={`chat-unban-${id}`} onClick={async () => (await callAdmin("post", `/players/${id}/chat-unban`, {}, "Chat ban removed")) && onChanged()}
                  className="px-3 py-1 rounded-lg border border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14]/15 font-heading text-xs uppercase tracking-widest">Remove chat ban</button>
              )}
            </div>
          )}
          {can("players.lock") && (
            <div className="space-y-1">
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#A89BCC]">Account lock</div>
              <RestrictForm testId={`lock-${id}`} label="Lock account"
                onApply={async (b) => (await callAdmin("post", `/players/${id}/lock`, b, "Account locked")) && onChanged()} />
              {p.lock && (
                <button data-testid={`unlock-${id}`} onClick={async () => (await callAdmin("post", `/players/${id}/unlock`, {}, "Account unlocked")) && onChanged()}
                  className="px-3 py-1 rounded-lg border border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14]/15 font-heading text-xs uppercase tracking-widest">Unlock account</button>
              )}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            {can("roles.manage") && (
              <button data-testid={`role-toggle-${id}`}
                onClick={async () => (await callAdmin("post", `/players/${id}/role`, { role: p.role === "moderator" ? null : "moderator" }, p.role === "moderator" ? "Moderator removed" : "Moderator appointed")) && onChanged()}
                className="px-3 py-1 rounded-lg border border-[#FFE600] text-[#FFE600] hover:bg-[#FFE600]/15 font-heading text-xs uppercase tracking-widest">
                {p.role === "moderator" ? "Remove moderator" : "Make moderator"}
              </button>
            )}
            {can("players.delete") && (
              <div className="flex items-center gap-2 ml-auto">
                <input data-testid={`delete-confirm-${id}`} value={confirmDelete} onChange={(e) => setConfirmDelete(e.target.value)}
                  placeholder={`Type ${p.account_name || "DELETE"} to confirm`}
                  className="px-2 py-1 rounded-lg bg-[#0A051A] border border-[#FF007F]/40 font-mono text-xs w-52" />
                <button data-testid={`delete-player-${id}`} disabled={confirmDelete !== (p.account_name || "DELETE")}
                  onClick={async () => (await callAdmin("delete", `/players/${id}`, { reason: "admin deletion" }, "Account deleted")) && onChanged(true)}
                  className="px-3 py-1 rounded-lg bg-[#FF007F]/20 border border-[#FF007F] text-[#FF007F] hover:bg-[#FF007F]/30 font-heading text-xs uppercase tracking-widest disabled:opacity-40">
                  Delete account
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
